import { useState, useEffect } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormDropDownList, FormNumericTextBox, FormSwitch, FormDatePicker } from "../FormComponents";
import { requiredValidator } from "../Validators";
import { PostVehicle } from "../../../data/interfaces";
import { getEmployees } from "../../../data/employeeData";
import { getPurchaseTypes, getVehicleTypes } from "../../../data/vehicleData";

interface AddVehicleFormProps {
  onVehicleSubmit: (vehicle: PostVehicle) => void;
  onErrorMessage?: string | null;
}

const AddVehicleForm = ({ onVehicleSubmit, onErrorMessage }: AddVehicleFormProps) => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [purchaseTypes] = useState(getPurchaseTypes());
  const [vehicleTypes] = useState(getVehicleTypes());

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeData = await getEmployees();
        const formattedEmployees = employeeData.map(emp => ({
          value: emp.employeeId,
          text: emp.employeeName
        }));
        setEmployees(formattedEmployees);
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  const handleSubmit = (dataItem: any) => {
    // Helper function to set time to 3AM to avoid timezone issues
    const setTimeTo3AM = (date: Date | null) => {
      if (!date) return null;
      const newDate = new Date(date);
      newDate.setHours(3, 0, 0, 0);
      return newDate.toISOString();
    };

    // Convert form data to API format
    const formattedData: PostVehicle = {
      registration: dataItem.registration,
      make: dataItem.make || null,
      model: dataItem.model || null,
      employeeId: dataItem.employeeId?.value || null,
      purchaseTypeId: dataItem.purchaseTypeId?.value || null,
      vehicleTypeId: dataItem.vehicleTypeId?.value || null,
      taxDate: setTimeTo3AM(dataItem.taxDate),
      motDate: setTimeTo3AM(dataItem.motDate),
      motBooked: dataItem.motBooked || false,
      serviceDate: setTimeTo3AM(dataItem.serviceDate),
      hasTracker: dataItem.hasTracker || false,
      serviceBooked: dataItem.serviceBooked || false,
      mileage: dataItem.mileage || null,
      leaseEndDate: setTimeTo3AM(dataItem.leaseEndDate),
      leaseMonthlyPayment: dataItem.leaseMonthlyPayment ?? null,
    };
    onVehicleSubmit(formattedData);
  };

  return (
    <div className="form__container">
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          registration: "",
          make: "",
          model: "",
          employeeId: null,
          purchaseTypeId: null,
          vehicleTypeId: null,
          taxDate: null,
          motDate: null,
          motBooked: false,
          serviceDate: null,
          hasTracker: false,
          serviceBooked: false,
          mileage: null,
          leaseEndDate: null,
          leaseMonthlyPayment: null,
        }}
        render={(formRenderProps) => (
          <FormElement style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }}>
            <fieldset className="k-form-fieldset">
              <legend className="k-form-legend">Vehicle Details</legend>

              <Field
                name="registration"
                component={FormInput}
                label="Registration *"
                validator={requiredValidator}
              />

              <Field
                name="make"
                component={FormInput}
                label="Make"
              />

              <Field
                name="model"
                component={FormInput}
                label="Model"
              />

              <Field
                name="employeeId"
                component={FormDropDownList}
                label="Assigned Employee"
                data={employees}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="purchaseTypeId"
                component={FormDropDownList}
                label="Purchase Type"
                data={purchaseTypes}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="vehicleTypeId"
                component={FormDropDownList}
                label="Vehicle Type"
                data={vehicleTypes}
                textField="text"
                dataItemKey="value"
              />

              <Field
                name="taxDate"
                component={FormDatePicker}
                label="Tax Due Date"
              />

              <Field
                name="motDate"
                component={FormDatePicker}
                label="MOT Due Date"
              />

              <Field
                name="motBooked"
                component={FormSwitch}
                label="MOT Booked"
              />

              <Field
                name="serviceDate"
                component={FormDatePicker}
                label="Service Date"
              />

              <Field
                name="mileage"
                component={FormNumericTextBox}
                label="Mileage"
              />

              <Field
                name="leaseEndDate"
                component={FormDatePicker}
                label="Lease End Date"
              />

              <Field
                name="leaseMonthlyPayment"
                component={FormNumericTextBox}
                label="Lease Monthly Payment"
                format="'£'#,##0.00"
                min={0}
              />

              <Field
                name="hasTracker"
                component={FormSwitch}
                label="Has Tracker"
              />

              <Field
                name="serviceBooked"
                component={FormSwitch}
                label="Service Booked"
              />
            </fieldset>

            {onErrorMessage && (
              <div className="error-message" style={{ color: "red", marginTop: "1rem" }}>
                {onErrorMessage}
              </div>
            )}

            <div className='button__container'>
              <button
                type="submit"
                disabled={!formRenderProps.allowSubmit}
                className='form__edit__button accept'
              >
                Add Vehicle
              </button>
            </div>
          </FormElement>
        )}
      />
    </div>
  );
};

export default AddVehicleForm;